import { Injectable } from '@angular/core';
import AgoraRTM from 'agora-rtm-sdk';
import { BehaviorSubject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AgoraRtmService {
	private client: any;
	private channel: any;

	public messages$ = new BehaviorSubject<{ from: string, text: string }[]>([]);
	public peers$ = new BehaviorSubject<string[]>([]);

	constructor() { }

	async init(appId: string) {
		// Classic RTM v1.x API
		this.client = AgoraRTM.createInstance(appId);

		// Incoming direct (peer-to-peer)
		this.client.on('MessageFromPeer', (msg: any, peerId: string) => {
			const list = [...this.messages$.value, { from: peerId, text: msg.text }];
			this.messages$.next(list);
		});
	}

	async login(uid: string, token?: string) {
		await this.client.login({ uid, token });
	}

	async joinChannel(channelName: string) {
		this.channel = this.client.createChannel(channelName);

		this.channel.on('ChannelMessage', (msg: any, memberId: string) => {
			const list = [...this.messages$.value, { from: memberId, text: msg.text }];
			this.messages$.next(list);
		});

		this.channel.on('MemberJoined', (memberId: string) => {
			const peers = Array.from(new Set([...this.peers$.value, memberId]));
			this.peers$.next(peers);
		});

		this.channel.on('MemberLeft', (memberId: string) => {
			this.peers$.next(this.peers$.value.filter(p => p !== memberId));
		});

		await this.channel.join();

		// v1.5 supports getMembers()
		const members = await this.channel.getMembers();
		this.peers$.next(members);
	}

	async leaveChannel() {
		if (!this.channel) return;
		await this.channel.leave();
		this.channel = null;
		this.peers$.next([]);
	}

	async logout() {
		if (this.client) await this.client.logout();
	}

	async sendChannelMessage(text: string) {
		await this.channel.sendMessage({ text });
		const list = [...this.messages$.value, { from: 'me', text }];
		this.messages$.next(list);
	}

	async sendPeerMessage(peerId: string, text: string) {
		await this.client.sendMessageToPeer({ text }, peerId);
		const list = [...this.messages$.value, { from: `→ ${peerId}`, text }];
		this.messages$.next(list);
	}
}
